import { Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { TextField } from '@fabric-ds/react';
import { v4 as uuidv4 } from 'uuid';

import { AutocompleteList } from './components/autocompleteList/AutocompleteList';
import styles from './Autocomplete.module.css';

export class Autocomplete extends Component {
    constructor(props) {
        super(props);
        this.delayedSearch = this.delayedSearch.bind(this);
        this.handleKeyboard = this.handleKeyboard.bind(this);
        this.handleSelection = this.handleSelection.bind(this);
        this.setFocusedSuggestion = this.setFocusedSuggestion.bind(this);

        this.state = {
            focusedSuggestion: 0,
            inputWidth: 0,
            gToken: uuidv4(),
        };
        this.timeoutReference = null;
    }

    componentDidMount() {
        this.updateWidth();
    }

    updateWidth() {
        const bounds = this.autocompleteInput && this.autocompleteInput.getBoundingClientRect();
        this.setState({
            inputWidth: bounds ? bounds.width || bounds.right - bounds.left : 0,
        });
    }

    delayedSearch(query) {
        if (this.timeoutReference) {
            clearTimeout(this.timeoutReference);
        }
        if (!query.trim()) {
            this.handleSelection();
            return;
        }
        if (query.trim().length < 2) {
            return;
        }
        this.timeoutReference = setTimeout(() => {
            this.props.fetch(Object.assign({}, this.props.filters, { query }), this.state.gToken);
        }, 300);
    }

    handleKeyboard(event) {
        switch (event.keyCode) {
            case 9: // tab
            case 13: {
                // enter
                if (!this.props.data.showSuggestions) {
                    break;
                }
                const selected = this.props.data.suggestions[this.state.focusedSuggestion];

                if (selected) {
                    this.handleSelection(selected);
                }
                break;
            }
            case 27: {
                // esc
                this.handleSelection();
                break;
            }
            case 38: {
                // up
                if (this.state.focusedSuggestion > 0) {
                    this.setFocusedSuggestion(this.state.focusedSuggestion - 1);
                }
                break;
            }
            case 40: {
                // down
                if (this.state.focusedSuggestion < this.props.data.suggestions.length - 1) {
                    this.setFocusedSuggestion(this.state.focusedSuggestion + 1);
                }
                break;
            }
            default: {
                this.delayedSearch(event.target.value);
            }
        }
    }

    handleChange(event) {
        if (this.props.data.selectedValue && event.target.value === '') {
            this.props.onFilterChanged({ query: '' });
        }
        this.props.updateAutocomplete(event.target.value);
    }

    setFocusedSuggestion(index) {
        this.setState({ focusedSuggestion: index });
    }

    handleSelection(autocompleteSelection = '', outSideClick = false) {
        this.setFocusedSuggestion(0);
        if (!autocompleteSelection) {
            this.props.updateAutocomplete(autocompleteSelection, null);
            this.props.onFilterChanged({ query: '' });
            return;
        }
        if (outSideClick && autocompleteSelection.numResults === 0) {
            this.props.updateAutocomplete('', null);
            return;
        }

        const filter = {};
        if (autocompleteSelection.type === 'search') {
            if (autocompleteSelection.numResults === 0) {
                return;
            }
            this.props.updateAutocomplete('', null);
            filter.query = autocompleteSelection.query;
        } else {
            const trackingInfo = {
                city: autocompleteSelection.primary,
                country: autocompleteSelection.country || autocompleteSelection.secondary,
                numResults: autocompleteSelection.numResults,
            };

            this.setState({ gToken: uuidv4() });
            this.props.updateAutocomplete(autocompleteSelection.destination, trackingInfo);

            if (this.props.filters && this.props.filters.query) {
                filter.query = '';
            }
        }

        if (autocompleteSelection.bounds) {
            Object.assign(filter, {
                lat_sw: autocompleteSelection.bounds.southwest_lat,
                lng_sw: autocompleteSelection.bounds.southwest_lng,
                lat_ne: autocompleteSelection.bounds.northeast_lat,
                lng_ne: autocompleteSelection.bounds.northeast_lng,
                country: autocompleteSelection.isCountry ? autocompleteSelection.country : null,
                destination: null,
            });
            this.props.onFilterChanged(filter);
        }
    }

    render() {
        return (
            <div className="input input--search mb-16" data-testid="autocomplete">
                <label className={cn(styles.label, 'md:mb-8')} htmlFor="fhh-search-field" role="heading" aria-level="2">
                    Hvor vil du reise?
                </label>
                <span role="combobox" aria-expanded={this.props.data.suggestions ? 'true' : 'false'} aria-owns="fhh-search-suggestions">
                    <TextField
                        data-testid="fhh-search-field"
                        id="fhh-search-field"
                        aria-controls={this.props.data.suggestions ? 'fhh-search-suggestions' : ''}
                        aria-activedescendant={this.props.data.suggestions ? `fhh-search-suggestion-${this.state.focusedSuggestion}` : ''}
                        type="search"
                        placeholder="Land, område eller sted"
                        autoComplete="off"
                        aria-autocomplete="list"
                        onKeyUp={this.handleKeyboard}
                        onChange={this.handleChange.bind(this)}
                        ref={(input) => (this.autocompleteInput = input)}
                        value={this.props.data.selectedValue ?? ''}
                    />
                </span>
                <AutocompleteList
                    data={this.props.data}
                    width={this.state.inputWidth}
                    focused={this.state.focusedSuggestion}
                    handleSelection={this.handleSelection}
                    setFocus={this.setFocusedSuggestion}
                />
            </div>
        );
    }
}

Autocomplete.propTypes = {
    data: PropTypes.object.isRequired,
    fetch: PropTypes.func.isRequired,
    updateAutocomplete: PropTypes.func.isRequired,
    onFilterChanged: PropTypes.func.isRequired,
    filters: PropTypes.object.isRequired,
};
